import React, { useCallback, useEffect, useState } from 'react';
import { useWebSockets } from '../../../WebSocketManager';
import { Heading } from '../../Heading/Heading';
import { DateFormat, DateFormats } from '../../DateFormat';
import { Download } from '../../Download';
import { StatusInfoLoading } from '../../StatusInfo/StatusInfoLoading';
import { StatusInfoError } from '../../StatusInfo';
import { useSonTranslation } from '../../../../translation/useSonTranslation';
import { useBrowserPushEventId } from '../../../ViewFetcher';
import LiveRegion from '../../../../components/atoms/LiveRegion/LiveRegion';
import { formatDate, TimeFormats } from '../../../../lib/DateFormat';
import { ISonURL } from '../../../../types/IUrl';

export type ReportItem = {
  executionId: string,
  status: 'successful' | 'failed' | 'inProgress';
  timestamp: string;
  reportFileUrl?: ISonURL;
  fileSizeInBytes: number;
  manuallyStarted: boolean;
  dryRun: boolean;
  fileExtension: string;
};

export function ReportsListElement({
  reportItem,
}: { reportItem: ReportItem }) {
  const { t } = useSonTranslation('RepotsList');
  const browserPushEventId = useBrowserPushEventId();
  const subscribe = useWebSockets();

  const [localReportItem, setLocalReportItem] = useState({ ...reportItem });

  const handleItemUpdate = useCallback(
    (item: ReportItem) => {
      if (reportItem.executionId === item.executionId && reportItem.status !== item.status) {
        setLocalReportItem({ ...item });
      }
    }, [reportItem.executionId, reportItem.status]);

  useEffect(() => {
    if (localReportItem.status === 'inProgress') {
      subscribe(browserPushEventId, handleItemUpdate);
    }
  }, []);

  const getExecutionTitle = (report: ReportItem): string => {
    if (report.dryRun) {
      return t('executionDryRun');
    } else if (report.manuallyStarted) {
      return t('executionManual');
    } else {
      return t('executionScheduled');
    }
  };

  return (
    <li className="page-membership-matching-rule-list__reportitem">
      <Heading className="page-membership-matching-rule-list__reportitem-headline font-bold-18-24" hierarchy={4}>
        <DateFormat date={localReportItem.timestamp} format={DateFormats.DATE_TIME} />
        ,&nbsp;
        {getExecutionTitle(reportItem)}
      </Heading>
      <div className="page-membership-matching-rule-list__reportitem-file-container">
        {localReportItem.status !== 'inProgress' && localReportItem.reportFileUrl
          ? (
              <Download
                fileExtension={reportItem.fileExtension}
                fileName={`${t('report')}, 
            ${formatDate(
                localReportItem.timestamp,
                TimeFormats.DAY_MONTH_YEAR_HOURS_MINUTES,
              )} ${t('DateFormat:oClock')}`}
                fileSizeBytes={localReportItem.fileSizeInBytes}
                url={localReportItem.reportFileUrl.url}
              />
            )
          : null}
        {localReportItem.status !== 'successful'
          ? (
              <div className="page-membership-matching-rule-list__reportitem-status-container">
                <LiveRegion invisible={false} htmlTag="div">
                  {localReportItem.status === 'inProgress'
                    ? (
                        <StatusInfoLoading message={t('executionPending')} />
                      )
                    : null}
                  {localReportItem.status === 'failed'
                    ? (
                        <StatusInfoError
                          modifier={['red']}

                          message={t('executionFailed')}
                          dataFormStatus="fail"
                        />
                      )
                    : null}
                </LiveRegion>
              </div>
            )
          : null}
      </div>
    </li>
  );
}
